import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { DimensionValue, Maybe, PendingApprovalEntry, PendingApprovalRow } from '@sit/client-shared';
import { TimesheetStoreItem } from '@web/types/redux/stores/timesheet.types';

const separatingToken = '<SIT_SEPARATING_TOKEN>';

const useApprovalsEntriesTableHeaders = (
  timesheet: TimesheetStoreItem,
  displayNames: {
    [x: string]: any;
    clientDisplayName?: Maybe<string> | undefined;
    projectDisplayName?: Maybe<string> | undefined;
    taskDisplayName?: Maybe<string> | undefined;
    itemDisplayName?: Maybe<string> | undefined;
    departmentDisplayName?: Maybe<string> | undefined;
    locationDisplayName?: Maybe<string> | undefined;
  },
  groupBy: string,
) => {
  const { _ } = useLingui();
  const headers = [];
  if (groupBy === 'date') {
    if (timesheet.showCustom) headers.push({ id: 'client', name: displayNames.clientDisplayName ?? _(msg`Customer`) });
    if (timesheet.showProject) headers.push({ id: 'project', name: displayNames.projectDisplayName ?? _(msg`Project`) });
  } else if (groupBy === 'project') {
    headers.push({ id: 'date', name: _(msg`Date`) });
    if (timesheet.showCustom) headers.push({ id: 'client', name: displayNames.clientDisplayName ?? _(msg`Customer`) });
  } else if (groupBy === 'client') {
    headers.push({ id: 'date', name: _(msg`Date`) });
    if (timesheet.showProject) headers.push({ id: 'project', name: displayNames.projectDisplayName ?? _(msg`Project`) });
  }
  if (timesheet.showTask) headers.push({ id: 'task', name: displayNames.taskDisplayName ?? _(msg`Task`) });
  if (timesheet.showItem) headers.push({ id: 'item', name: displayNames.itemDisplayName ?? _(msg`Item`) });
  if (timesheet.showTimeType) headers.push({ id: 'timeType', name: _(msg`Time type`) });
  if (timesheet.showDepartmentLocation) {
    headers.push(
      { id: 'department', name: displayNames.departmentDisplayName ?? _(msg`Department`) },
      { id: 'location', name: displayNames.locationDisplayName ?? _(msg`Location`) },
    );
  }
  headers.push({ id: 'billable', name: _(msg`Billable`) });
  const commonHeaders = [
    {
      id: 'totalHours',
      name: _(msg`Total hours`),
    },
    {
      id: 'status',
      name: _(msg`State`),
    },
    {
      id: 'nextApprovers',
      name: _(msg`Next approver(s)`),
    },
    {
      id: 'actionPopover',
      name: null,
    },
  ];
  return [...headers, ...commonHeaders];
};

const useApprovalsTimesheetsTableHeaders = (groupBy: string) => {
  const { _ } = useLingui();
  if (groupBy === 'employee') {
    return [
      {
        id: 'period',
        name: _(msg`Timesheet period`),
      },
      {
        id: 'title',
        name: _(msg`Description`),
      },
      {
        id: 'totalHours',
        name: _(msg`Total hours`),
      },
      {
        id: 'totalBillable',
        name: _(msg`Billable hours`),
      },
      {
        id: 'totalNonbillable',
        name: _(msg`Non-billable hours`),
      },
      {
        id: 'entries',
        name: _(msg`Entries`),
      },
      {
        id: 'status',
        name: _(msg`State`),
      },
    ];
  }
  return [
    {
      id: 'title',
      name: _(msg`Employee name`),
    },
    {
      id: 'totalHours',
      name: _(msg`Total hours`),
    },
    {
      id: 'totalBillable',
      name: _(msg`Billable hours`),
    },
    {
      id: 'totalNonbillable',
      name: _(msg`Non-billable hours`),
    },
    {
      id: 'entries',
      name: _(msg`Entries`),
    },
  ];
};

const getNameWithIntacctIdifTrue = (obj: { externalId: string; name: string } | null | undefined, showIntacctId: boolean) => {
  if (!obj) return '-';
  if (showIntacctId) return `${obj.externalId}--${obj.name}`;
  return `${obj.name}`;
};

const readDimension = (entry: PendingApprovalEntry, name: string, showIntacctId: boolean) => {
  const lower = name.toLowerCase();
  const dim = entry.dimensions?.find((d) => Object.keys(d)[0].toLowerCase() === lower || d.dimension?.toLowerCase() === lower);
  if (!dim) return null;
  const key = (lower.charAt(0).toUpperCase() + lower.slice(1)) as keyof DimensionValue;
  return getNameWithIntacctIdifTrue(dim[key] || dim, showIntacctId);
};

const getGroupId = ({ rowId, employeeId }: PendingApprovalRow) => {
  if (!employeeId) {
    return rowId;
  }
  return `${rowId}:${employeeId}`;
};

export {
  useApprovalsEntriesTableHeaders,
  getGroupId,
  getNameWithIntacctIdifTrue,
  readDimension,
  separatingToken,
  useApprovalsTimesheetsTableHeaders,
};
